import { NJButton, NJTag } from "@engie-group/fluid-design-system-react";
import { useChat } from "./ChatProvider";
import { MarkdownMessage } from "./MarkdownMessage";
// import { MessageFeedback } from "./MessageFeedback"; // désactivé temporairement
import { Reasoning } from "./Reasoning";
import type { ChatMessage, ChatSource } from "./types";

/** Assistant answer block — reasoning, markdown content, feedback and a sources button
 * (mirrors the legacy `AssistantMessage`). Reasoning/sources light up when a real agent
 * provides them via the SSE stream; they stay hidden otherwise. */
function formatWhen(at: string): string {
  if (!at) return "";
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

const FOLLOW_UP_MARKER =
  /^\s*(?:#{1,6}\s*)?(?:[*_]{1,2}\s*)?(?:pour\s+)?aller\s+plus\s+loin\b.*$/im;

/** Splits an answer at the "pour aller plus loin" block: the text up to and including the marker
 * line stays as `body`; every non-empty line after it is a suggested follow-up question. */
function splitFollowUps(content: string): {
  body: string;
  questions: string[];
} {
  const match = FOLLOW_UP_MARKER.exec(content);
  if (!match) return { body: content, questions: [] };
  const questions = content
    .slice(match.index + match[0].length)
    .split("\n")
    .map((line) =>
      line
        .replace(/^\s*(?:[-*•>]|\d+[.)])\s+/, "")
        .replace(/[*_`]/g, "")
        .trim(),
    )
    .filter(Boolean);
  return {
    body: content.slice(0, match.index + match[0].length).trimEnd(),
    questions,
  };
}

export function AssistantMessage({
  message,
  agentName,
  streaming,
  onOpenSources,
}: {
  message: ChatMessage;
  agentName?: string;
  streaming?: boolean;
  onOpenSources: (sources: ChatSource[]) => void;
}) {
  const { send, isSending } = useChat();
  const { body, questions } = splitFollowUps(message.content ?? "");
  const sources = message.sources ?? [];
  const when = formatWhen(message.at);

  return (
    <div className="flex justify-start py-1">
      <div
        className="flex max-w-[80%] flex-col gap-2 border px-3 py-2 text-nj-neutral-primary-default"
        style={{
          backgroundColor:
            "var(--nj-semantic-color-background-status-success-secondary-default)",
          borderColor:
            "var(--nj-semantic-color-border-status-success-subtle-default)",
        }}
      >
        <div className="flex items-center gap-2 text-xs text-nj-neutral-secondary-default">
          <strong>{agentName || "Agent"}</strong>
          {when && <span>{when}</span>}
        </div>
        {message.reasoningContent && (
          <Reasoning content={message.reasoningContent} streaming={streaming} />
        )}

        {body && <MarkdownMessage content={body} />}

        {!streaming && questions.length > 0 && (
          <div className="flex flex-col items-start gap-8">
            {questions.map((question) => (
              <button
                key={question}
                type="button"
                disabled={isSending}
                className="cursor-pointer text-left disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => send(question, [])}
              >
                <NJTag label={question} />
              </button>
            ))}
          </div>
        )}

        {!streaming && message.content && (
          <div className="flex flex-col gap-2">
            {/* Feedback pouce en haut/bas désactivé temporairement (purement front, pas encore branché) */}
            {/* <MessageFeedback content={message.content} /> */}
            {sources.length > 0 && (
              <NJButton
                emphasis="subtle"
                size="xsmall"
                label={`SOURCE${sources.length > 1 ? "S" : ""} (${sources.length})`}
                onClick={() => onOpenSources(sources)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
